import { Milestone } from '../types';
import {
  getEnhancedCellStyling as getCellStyling,
  formatEnhancedCellDisplay as formatMilestoneDisplayText,
  getEnhancedMilestoneIcon,
  getDateForPastCheck,
  MILESTONE_ICONS
} from '../../../components/SlideOutPanel/Milestones/milestoneConfigEnhanced';
import {
  convertMilestoneTimestampToLocal,
  normalizeTimezoneInString
} from '../../../../utils/time';
import { isMilestoneDateInPast } from '../utils/milestoneSortUtils';
import { getMilestoneDateTextStyle } from '../../../components/SlideOutPanel/Milestones/milestoneDisplayUtils';

/**
 * Get milestone cell properties for rendering
 * Encapsulates all logic from SlideoutPanel for milestone display
 */
export const getMilestoneCellProps = (milestone: Milestone | null) => {
  if (!milestone) {
    // No milestone entry at all for this column on this row (e.g. RR1/RR2 on a
    // PCO row, or RR on a non-PCO row) — this moment type simply doesn't use
    // this milestone. Render it identically to an explicit "Milestone Not
    // Required" cell rather than a bare, unstyled cell.
    return {
      statusKey: 'NOT_REQUIRED',
      isTBD: false,
      displayText: '',
      bgStyle: getCellStyling('NOT_REQUIRED', {
        approvalCondition: 'NO Approval Required',
        reviewType: 'Milestone Not Required'
      }),
      iconName: null,
      hasRecordingLink: false,
      recordingLink: null,
      hasDocumentLink: false,
      documentLink: null,
      dateTextStyle: {}
    };
  }

  const statusKey = getStatusKey(milestone);
  const isTBD = isTBDMilestone(milestone);
  const displayText = getMilestoneDisplayText(milestone, statusKey, isTBD);
  const bgStyle = getCellBackgroundStyle(milestone, statusKey);
  const iconName = getIconName(milestone, statusKey);
  const dateTextStyle = getMilestoneDateTextStyle(
    {
      isEdited: milestone.isEdited,
      status: milestone.status,
      date: milestone.date,
      secondaryDate: milestone.secondaryDate
    },
    Boolean(displayText?.trim())
  );

  return {
    statusKey,
    isTBD,
    displayText,
    bgStyle,
    dateTextStyle,
    iconName,
    hasRecordingLink: !!milestone.recordingLink,
    recordingLink: milestone.recordingLink,
    hasDocumentLink:
      !!milestone.documentLink &&
      displayText &&
      displayText.toUpperCase() !== 'N/A' &&
      displayText.toUpperCase() !== 'TBD',
    documentLink: milestone.documentLink
  };
};

// ============= Helper Functions =============

/**
 * Determine the status key for a milestone
 */
const getStatusKey = (milestone: Milestone): string => {
  const { reviewType, status } = milestone;

  // Handle "Not Required" review types
  if (
    reviewType === 'Not Required' ||
    reviewType === 'Milestone Not Required'
  ) {
    return 'NOT_REQUIRED';
  }

  // Convert status to uppercase or default to TBD
  if (status && typeof status === 'string') {
    return status.toUpperCase();
  }

  return 'TBD';
};

/**
 * Check if milestone should display as TBD
 */
const isTBDMilestone = (milestone: Milestone): boolean => {
  const { reviewType, status } = milestone;

  return (
    reviewType === 'TBD' ||
    (status === 'TBD' &&
      reviewType !== 'Milestone Not Required' &&
      reviewType !== 'Not Required')
  );
};

/**
 * Get milestone display text with proper formatting
 * Replicates SlideoutPanel's preprocessing logic for dates/times
 */
const getMilestoneDisplayText = (
  milestone: Milestone,
  statusKey: string,
  isTBD: boolean
): string => {
  if (isTBD) return '';

  // For NOT_REQUIRED status, always return empty string (show only background color)
  if (statusKey === 'NOT_REQUIRED') {
    return '';
  }

  const { reviewType, status } = milestone;
  const approvalCondition = milestone.milestoneAttributes?.approvalRequired
    ? 'Approval Required'
    : 'NO Approval Required';

  // === TIME CONVERSION (matches SlideoutPanel logic) ===
  // Priority 1: Calculate time from UTC timestamp for cross-timezone compatibility
  const timestampToUse =
    milestone.displayScheduleDateTime ||
    milestone.meetingScheduledOn ||
    undefined;

  // Convert UTC timestamp to local time using the shared utility function
  const converted = convertMilestoneTimestampToLocal(
    timestampToUse,
    milestone.meetingScheduledAt || undefined,
    false, // Don't include date (handled separately)
    {
      defaultDurationMinutes: 90, // Default 1.5 hours
      formatWithSpaces: false, // Format without spaces
      use24HourFormat: true, // Use 24-hour format: "14:30" instead of "2:30 PM"
      includeTimezone: false // Don't include timezone
    }
  );

  let timeString = '';
  if (converted) {
    timeString = converted.timeString;
  }
  // Priority 2: Fallback to stored time string
  else if (milestone.meetingScheduledAt) {
    timeString = milestone.meetingScheduledAt;
  }

  // Normalize timezone notation (replace GMT+5:30 with IST, etc.)
  const normalizedTimeString = normalizeTimezoneInString(timeString);

  // === DATE CONVERSION (matches SlideoutPanel logic) ===
  // For MEETING_SCHEDULED status, extract date from UTC timestamp
  let displayDate = milestone.date;
  if (statusKey === 'MEETING_SCHEDULED') {
    const dateTimestamp =
      milestone.displayScheduleDateTime || milestone.meetingScheduledOn;
    if (dateTimestamp && /^\d{4}-\d{2}-\d{2}T/.test(dateTimestamp)) {
      try {
        // Convert UTC timestamp to user's local date
        const utcDate = new Date(dateTimestamp);
        if (!isNaN(utcDate.getTime())) {
          // Format in user's local timezone
          const year = utcDate.getFullYear();
          const month = String(utcDate.getMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getDate()).padStart(2, '0');
          displayDate = `${year}-${month}-${day}`;
        }
      } catch (e) {
        // Fallback to simple extraction if conversion fails
        const isoMatch = dateTimestamp.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) {
          displayDate = isoMatch[1];
        }
      }
    }
  }

  // Use enhanced formatting with preprocessed data (matches SlideoutPanel behavior)
  return formatMilestoneDisplayText(status || '', {
    date: displayDate,
    meetingScheduledAt: normalizedTimeString,
    secondaryDate: milestone.secondaryDate,
    approvalCondition,
    reviewType: reviewType || 'Meeting'
  });
};

/**
 * Get cell background and text color style based on milestone status
 */
const getCellBackgroundStyle = (
  milestone: Milestone,
  statusKey: string
): Record<string, any> => {
  const approvalCondition = milestone.milestoneAttributes?.approvalRequired
    ? 'Approval Required'
    : 'NO Approval Required';

  const cellStyling = getCellStyling(statusKey, {
    approvalCondition,
    reviewType: milestone.reviewType || 'Meeting'
  });

  const style: Record<string, any> = cellStyling.background
    ? { background: cellStyling.background }
    : { backgroundColor: cellStyling.backgroundColor };

  // Include text color if specified (e.g., red for APPROVED_FOLLOW_UP)
  if (cellStyling.color) {
    style.color = cellStyling.color;
  }

  // Check if date is in past for non-approved/complete statuses
  // These are the "end states" with green backgrounds - skip date check for these
  const isEndState = [
    'APPROVED',
    'APPROVED_ASYNC',
    'COMPLETE',
    'COMPLETE_ASYNC',
    'COMPLETE_ASYNC_ALT',
    'RE_APPROVED',
    'RE_APPROVED_ASYNC'
  ].includes(statusKey);

  // Use the date that is displayed in the cell (secondary when shown, else primary)
  const dateToCheck = getDateForPastCheck(milestone, statusKey);
  if (!isEndState && dateToCheck) {
    try {
      // Check if milestone date is in the past using smart logic:
      // - For month/quarter formats (e.g., "FEB 2026"), only past if we're AFTER that period
      // - For specific dates (e.g., "2026-02-15"), past if before today
      if (isMilestoneDateInPast(dateToCheck)) {
        style.color = '#D7373F'; // Red color for past dates
      }
    } catch (e) {
      // Invalid date format, ignore
      console.warn('Invalid date format for milestone:', dateToCheck);
    }
  }

  return style;
};

/**
 * Get icon name for the milestone status
 */
const getIconName = (
  milestone: Milestone,
  statusKey: string
): string | null => {
  const approvalCondition = milestone.milestoneAttributes?.approvalRequired
    ? 'Approval Required'
    : 'NO Approval Required';

  const enhancedIcon = getEnhancedMilestoneIcon(statusKey, {
    approvalCondition,
    reviewType: milestone.reviewType || 'Meeting'
  });

  return enhancedIcon && MILESTONE_ICONS[enhancedIcon]
    ? MILESTONE_ICONS[enhancedIcon]
    : null;
};
